import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';

const TIER_COLOR = { cold: '#6366f1', warm: '#f59e0b', hot: '#ef4444', sunset: '#9ca3af' };

// The nurture copy customers write is deliberately rendered in the same serif
// the emails themselves use, so what they edit looks like what sends. Kept
// identical between the read-only and editing views for the same reason.
const BODY_TYPE = {
  fontSize: 13, lineHeight: 1.7, fontFamily: 'Georgia, serif',
};

// Must stay in step with buildMergeVars() in backend/src/services/email.js.
// Anything outside this list is left in the sent email as literal text by
// renderTemplate, so a typo like {{frist_name}} reaches the lead verbatim.
const KNOWN_PLACEHOLDERS = [
  'first_name', 'last_name', 'email', 'phone', 'industry', 'province',
  'service_interest', 'owner_name', 'business_name', 'business_address',
  'booking_link', 'lead_magnet_link', 'reply_to_email', 'unsubscribe_link',
];

const PLACEHOLDER_RE = /\{\{(\w+)\}\}/g;

function findPlaceholders(text) {
  return Array.from(String(text || '').matchAll(PLACEHOLDER_RE), m => m[1]);
}

// Split into plain-text and placeholder runs so the highlight layer behind the
// textarea can paint a chip behind each {{token}}.
function segmentText(text) {
  const out = [];
  let last = 0;
  for (const m of String(text || '').matchAll(PLACEHOLDER_RE)) {
    if (m.index > last) out.push({ text: text.slice(last, m.index) });
    out.push({ text: m[0], token: m[1] });
    last = m.index + m[0].length;
  }
  if (last < String(text || '').length) out.push({ text: text.slice(last) });
  return out;
}

export default function Sequences() {
  const [sequences, setSequences] = useState([]);
  const [selected, setSelected]   = useState(null);
  const [steps, setSteps]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft]         = useState({});
  const [saving, setSaving]       = useState(false);
  const [saveError, setSaveError] = useState('');
  // Only one step is ever in edit mode, so a single backdrop ref is enough.
  const backdropRef = useRef(null);

  useEffect(() => {
    axios.get('/api/sequences').then(r => {
      setSequences(r.data);
    }).catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const loadSequence = async (seq) => {
    setSelected(seq);
    setSteps([]);
    setEditingId(null);
    setSaveError('');
    try {
      const { data } = await axios.get(`/api/sequences/${seq.id}`);
      setSteps(data.steps || []);
    } catch {
      setError(true);
    }
  };

  // Only an account's own sequences are editable — PATCH is scoped to
  // s.account_id = req.account.id, so offering Edit on a system default
  // would just 404. Accounts get their own copies cloned at signup.
  const editable = !!selected?.account_id;

  const startEdit = (step) => {
    setSaveError('');
    setEditingId(step.id);
    setDraft({
      subject:    step.subject   || '',
      body_text:  step.body_text || '',
      sms_body:   step.sms_body  || '',
      delay_days: step.delay_days,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft({});
    setSaveError('');
  };

  const saveStep = async (step) => {
    const delay = Number(draft.delay_days);
    if (!Number.isInteger(delay) || delay < 0) {
      setSaveError('Day must be a whole number, 0 or greater.');
      return;
    }

    const isEmail = step.channel === 'email';
    const bodyValue = isEmail ? draft.body_text : draft.sms_body;

    // A placeholder Vela doesn't recognise is not a no-op — renderTemplate
    // leaves it in the email verbatim, so the lead receives the raw
    // "{{frist_name}}" text. Refuse the save rather than let that send.
    const unknown = [...new Set(
      [...findPlaceholders(bodyValue), ...(isEmail ? findPlaceholders(draft.subject) : [])]
        .filter(name => !KNOWN_PLACEHOLDERS.includes(name))
    )];
    if (unknown.length) {
      setSaveError(
        `Unknown placeholder${unknown.length > 1 ? 's' : ''}: ${unknown.map(n => `{{${n}}}`).join(', ')}. ` +
        `These would be sent to your lead as raw text. Available placeholders: ${KNOWN_PLACEHOLDERS.map(n => `{{${n}}}`).join(', ')}.`
      );
      return;
    }

    // CASL requires a working unsubscribe mechanism in every commercial
    // email. Deleting the placeholder would silently remove it.
    if (isEmail && !findPlaceholders(bodyValue).includes('unsubscribe_link')) {
      setSaveError(
        'This email must include {{unsubscribe_link}} — anti-spam law (CASL) requires ' +
        'every message to carry a working unsubscribe link. Add it back before saving.'
      );
      return;
    }

    setSaving(true);
    setSaveError('');
    try {
      // The endpoint COALESCEs every field, so only send what this channel
      // actually uses — an email step has no sms_body and vice versa.
      const payload = step.channel === 'email'
        ? { subject: draft.subject, body_text: draft.body_text, delay_days: delay }
        : { sms_body: draft.sms_body, delay_days: delay };

      const { data } = await axios.patch(
        `/api/sequences/${selected.id}/steps/${step.id}`, payload
      );
      setSteps(prev => prev.map(s => (s.id === step.id ? data : s)));
      setEditingId(null);
      setDraft({});
    } catch (err) {
      setSaveError(err.response?.data?.error || 'Could not save this step. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const fieldStyle = {
    width: '100%', boxSizing: 'border-box',
    background: 'var(--card-bg)', color: 'var(--vela-text)',
    border: '1px solid var(--border)', borderRadius: 6,
    padding: '8px 10px', fontSize: 13,
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700 }}>Email Sequences</h1>
        <p style={{ color: 'var(--text-muted)', marginTop: 2 }}>
          Pre-built nurture sequences for every tier. Click a sequence to read its
          emails — the ones belonging to your account can be edited.
        </p>
      </div>

      {error && (
        <div style={{ background: 'rgba(220,38,38,0.15)', color: 'var(--danger)', borderRadius: 7, padding: '10px 14px', marginBottom: 16, fontSize: 13 }}>
          Couldn't load sequence data. Please refresh the page.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 20 }}>

        {/* Sequence list */}
        <div>
          {loading && <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading...</p>}
          {sequences.map(seq => (
            <div key={seq.id}
              onClick={() => loadSequence(seq)}
              style={{
                background: selected?.id === seq.id ? 'var(--primary-light)' : 'var(--card-bg)',
                border: `1px solid ${selected?.id === seq.id ? 'var(--primary)' : 'var(--border)'}`,
                borderRadius: 9, padding: '14px 16px', marginBottom: 10, cursor: 'pointer',
              }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 600, fontSize: 14 }}>{seq.name}</span>
                <span style={{
                  background: TIER_COLOR[seq.tier] + '22', color: TIER_COLOR[seq.tier],
                  fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
                  textTransform: 'uppercase',
                }}>{seq.tier}</span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                {seq.step_count} steps · {seq.account_id ? 'Custom' : 'System default'}
              </div>
            </div>
          ))}
        </div>

        {/* Step viewer / editor */}
        <div>
          {!selected && (
            <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 10, padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
              Select a sequence to view its steps
            </div>
          )}

          {selected && (
            <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 10, padding: 24 }}>
              <h2 style={{ fontSize: 17, fontWeight: 700, marginBottom: 6 }}>{selected.name}</h2>
              <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 24 }}>
                {editable
                  ? 'This sequence belongs to your account — edit any step below and your changes apply to future sends.'
                  : 'This is a system default sequence and can’t be edited. Create a custom sequence to override it for your account.'}
              </p>

              {saveError && (
                <div style={{ background: 'rgba(220,38,38,0.15)', color: 'var(--danger)', borderRadius: 7, padding: '10px 14px', marginBottom: 16, fontSize: 13 }}>
                  {saveError}
                </div>
              )}

              {steps.map(step => {
                const isEditing = editingId === step.id;
                return (
                  <div key={step.id} style={{
                    border: `1px solid ${isEditing ? 'var(--primary)' : 'var(--border)'}`,
                    borderRadius: 9, marginBottom: 16, overflow: 'hidden',
                  }}>
                    <div style={{
                      background: 'rgba(255,255,255,0.03)', padding: '10px 16px',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      borderBottom: '1px solid var(--border)', gap: 12,
                    }}>
                      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                        <span style={{ fontWeight: 700, fontSize: 13 }}>Step {step.step_number}</span>
                        <span style={{
                          background: step.channel === 'email' ? 'var(--primary-light)' : 'rgba(34,197,94,0.15)',
                          color: step.channel === 'email' ? 'var(--primary)' : 'var(--success)',
                          fontSize: 11, padding: '2px 7px', borderRadius: 4, fontWeight: 600,
                        }}>{step.channel.toUpperCase()}</span>
                      </div>

                      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        {isEditing ? (
                          <>
                            <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                              Day{' '}
                              <input
                                type="number" min="0"
                                value={draft.delay_days}
                                onChange={e => setDraft({ ...draft, delay_days: e.target.value })}
                                style={{ ...fieldStyle, width: 64, padding: '4px 6px', display: 'inline-block' }}
                              />
                            </label>
                            <button onClick={cancelEdit} disabled={saving} style={{
                              background: 'transparent', color: 'var(--text-muted)',
                              border: '1px solid var(--border)', borderRadius: 6,
                              padding: '5px 12px', fontSize: 12, cursor: 'pointer',
                            }}>Cancel</button>
                            <button onClick={() => saveStep(step)} disabled={saving} style={{
                              background: 'var(--primary)', color: 'var(--primary-contrast)', border: 'none',
                              borderRadius: 6, padding: '5px 14px', fontSize: 12,
                              fontWeight: 600, cursor: saving ? 'default' : 'pointer',
                            }}>{saving ? 'Saving…' : 'Save'}</button>
                          </>
                        ) : (
                          <>
                            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                              Day {step.delay_days}
                            </span>
                            {editable && (
                              <button onClick={() => startEdit(step)} style={{
                                background: 'transparent', color: 'var(--primary)',
                                border: '1px solid var(--primary)', borderRadius: 6,
                                padding: '4px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                              }}>Edit</button>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    <div style={{ padding: '14px 16px' }}>
                      {isEditing ? (
                        <>
                          {step.channel === 'email' && (
                            <div style={{ marginBottom: 12 }}>
                              <label style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginBottom: 5 }}>
                                Subject
                              </label>
                              <input
                                value={draft.subject}
                                onChange={e => setDraft({ ...draft, subject: e.target.value })}
                                style={fieldStyle}
                              />
                            </div>
                          )}
                          <label style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginBottom: 5 }}>
                            {step.channel === 'email' ? 'Body' : 'Message'}
                          </label>

                          {/*
                            A highlight layer sits behind a real <textarea> rather
                            than replacing it with a contentEditable: every native
                            behaviour (paste, undo, spellcheck, mobile keyboards)
                            is preserved, and the worst case is a cosmetic
                            misalignment instead of corrupted customer copy. Both
                            layers must share typography, padding and border width
                            or the chips drift away from the text.
                          */}
                          <div style={{ position: 'relative' }}>
                            <div ref={backdropRef} aria-hidden="true" style={{
                              ...fieldStyle, ...BODY_TYPE,
                              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                              overflow: 'hidden', pointerEvents: 'none',
                              color: 'transparent', borderColor: 'transparent',
                              whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                            }}>
                              {segmentText(step.channel === 'email' ? draft.body_text : draft.sms_body)
                                .map((seg, i) => seg.token ? (
                                  <span key={i} style={{
                                    borderRadius: 3,
                                    background: KNOWN_PLACEHOLDERS.includes(seg.token)
                                      ? 'rgba(60,157,160,0.35)'
                                      : 'rgba(220,38,38,0.38)',
                                  }}>{seg.text}</span>
                                ) : <span key={i}>{seg.text}</span>)}
                              {'\n'}
                            </div>
                            <textarea
                              rows={step.channel === 'email' ? 14 : 4}
                              value={step.channel === 'email' ? draft.body_text : draft.sms_body}
                              onChange={e => setDraft(step.channel === 'email'
                                ? { ...draft, body_text: e.target.value }
                                : { ...draft, sms_body: e.target.value })}
                              onScroll={e => {
                                if (backdropRef.current) backdropRef.current.scrollTop = e.target.scrollTop;
                              }}
                              style={{
                                ...fieldStyle, ...BODY_TYPE, resize: 'vertical',
                                position: 'relative', background: 'transparent',
                              }}
                            />
                          </div>

                          <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
                            Highlighted words like <code>{'{{first_name}}'}</code> are filled in automatically
                            when the email sends — write around them, don't retype them.
                            A <span style={{ background: 'rgba(220,38,38,0.38)', borderRadius: 3, padding: '0 3px' }}>red</span>{' '}
                            highlight means Vela doesn't recognise that placeholder and won't save it.
                            {step.channel === 'email' && <> <code>{'{{unsubscribe_link}}'}</code> must stay — it's required by law.</>}
                          </p>
                        </>
                      ) : (
                        <>
                          {step.subject && (
                            <div style={{ fontWeight: 600, marginBottom: 10, fontSize: 14 }}>
                              Subject: {step.subject}
                            </div>
                          )}
                          <pre style={{
                            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                            color: 'var(--vela-text-mid)', margin: 0, ...BODY_TYPE,
                          }}>
                            {step.body_text || step.sms_body}
                          </pre>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
